// Sun position for Aurora Village (68.65°N, 27.51°E) and the sky palette that follows it.
// Accurate to a fraction of a degree, enough for lighting and twilight phases.
import * as THREE from 'three/webgpu';
import { Fn, uniform, renderGroup, float, vec3, vec4, mix, pow, max, clamp, smoothstep, exp, sqrt, dot, normalize, step, select, fwidth, If, positionGeometry, modelViewMatrix, cameraProjectionMatrix } from 'three/tsl';

export const SITE = { lat: 68.6508, lon: 27.5079, utcOffset: 2 }; // Finland, EET (no DST in winter)

const RAD = Math.PI / 180;

/** Altitude and azimuth (degrees, azimuth clockwise from north) for a UTC timestamp. */
export function sunPosition(ms, lat = SITE.lat, lon = SITE.lon) {
  const d = (ms - Date.UTC(2000, 0, 1, 12)) / 86400000;
  const g = (357.529 + 0.98560028 * d) * RAD;
  const q = 280.459 + 0.98564736 * d;
  const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD;
  const e = (23.439 - 0.00000036 * d) * RAD;
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L));
  const dec = Math.asin(Math.sin(e) * Math.sin(L));
  const gmst = ((18.697374558 + 24.06570982441908 * d) % 24) * 15 * RAD;
  const H = gmst + lon * RAD - ra;
  const phi = lat * RAD;
  const alt = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
  const az = Math.atan2(-Math.sin(H), Math.tan(dec) * Math.cos(phi) - Math.sin(phi) * Math.cos(H));
  return { alt: alt / RAD, az: ((az / RAD) + 360) % 360 };
}

/** Local sidereal time in degrees (where the sky's 0h meridian stands) for a UTC timestamp: turns the stars. */
export function siderealDeg(ms, lon = SITE.lon) {
  const d = (ms - Date.UTC(2000, 0, 1, 12)) / 86400000;
  return ((((18.697374558 + 24.06570982441908 * d) % 24) * 15 + lon) % 360 + 360) % 360;
}

/** Moon altitude and azimuth (degrees, as sunPosition), to about 0.3 deg: the main lunar terms, topocentric. */
export function moonPosition(ms, lat = SITE.lat, lon = SITE.lon) {
  const d = (ms - Date.UTC(2000, 0, 1, 12)) / 86400000;
  const L = 218.316 + 13.176396 * d, M = (134.963 + 13.064993 * d) * RAD, F = (93.272 + 13.22935 * d) * RAD;
  const D = (297.850 + 12.190749 * d) * RAD, Ms = (357.529 + 0.98560028 * d) * RAD;
  // ecliptic longitude: equation of centre, evection, variation, annual equation
  const l = (L + 6.289 * Math.sin(M) + 1.274 * Math.sin(2 * D - M) + 0.658 * Math.sin(2 * D) - 0.186 * Math.sin(Ms)) * RAD;
  const b = 5.128 * Math.sin(F) * RAD, e = 23.4397 * RAD;
  const ra = Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l));
  const dec = Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l));
  const H = siderealDeg(ms, lon) * RAD - ra, phi = lat * RAD;
  const alt = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
  const az = Math.atan2(-Math.sin(H), Math.tan(dec) * Math.cos(phi) - Math.sin(phi) * Math.cos(H));
  return { alt: alt / RAD - 0.95 * Math.cos(alt), az: ((az / RAD) + 360) % 360 }; // 0.95 deg: parallax, seen from the ground
}

// ---- WS-77 the year (begin): the sky (sun, moon, stars, Live clouds) is drawn for a date and a YEAR. A date chosen on
// the map (a preset's, a visitor's, a tour's) keeps the map's fixed year (site.year, 2026 when absent: the approved
// looks of its dates never change); a date that follows the clock (time.mode `real`) takes the real year at the site,
// so the moon and the Live clouds stay right after New Year. main.js applyTime writes state.year; every reader of the
// map's date takes it from there (mapYear).
export const FIXED_YEAR = 2026;
/** The year of the map's date (state.year from main.js applyTime; the fixed year before it ran). */
export const mapYear = (s) => (Number.isFinite(s?.year) ? s.year : FIXED_YEAR);
/** The year at the site now (utcOffset in hours). */
export const clockYear = (utcOffset = SITE.utcOffset, now = Date.now()) => new Date(now + utcOffset * 3600000).getUTCFullYear();
// ---- WS-77 the year (end)

/** Local (EET) date + decimal hour -> UTC ms. */
export function localToUTC(year, month, day, hour) {
  return Date.UTC(year, month - 1, day, 0, 0, 0) + (hour - SITE.utcOffset) * 3600000;
}

/** Unit vector towards the sun in scene coordinates (x east, y up, z south). */
export function sunVector({ alt, az }, out = new THREE.Vector3()) {
  const a = alt * RAD, z = az * RAD;
  return out.set(Math.sin(z) * Math.cos(a), Math.sin(a), -Math.cos(z) * Math.cos(a));
}

/** The phase of the day for a sun altitude, as an id: the interface names it (interface key `phase.<id>`). */
export function phase(alt) {
  if (alt > 6) return 'daylight';
  if (alt > 0) return 'golden';
  if (alt > -6) return 'civil';
  if (alt > -12) return 'nautical';
  if (alt > -18) return 'astronomical';
  return 'night';
}

// Sky keyframes by sun altitude (degrees): zenith, horizon, sun-side glow, light colour/intensity. The two night keys
// (-24 and -14 deg) are made per night blue and sky (nightKeys below); blue hour (-8 deg and up) is fixed.
const DAY_KEYS = [
  { alt: -8, zenith: '#0f1d42', horizon: '#3b4f86', glow: '#9a6a8a', belt: '#4a4f84', hemiSky: '#4a6aa6', hemiGround: '#3a4c74', hemi: 0.85, sun: 0 },
  { alt: -3, zenith: '#2a4580', horizon: '#c99ab0', glow: '#ffa27a', belt: '#9e8fbe', hemiSky: '#7d97cc', hemiGround: '#8a92b8', hemi: 1.05, sun: 0 },
  { alt: 1, zenith: '#46699f', horizon: '#f1c2a7', glow: '#ff9a55', belt: '#c7a8c6', hemiSky: '#a9bde0', hemiGround: '#c4c3d2', hemi: 1.15, sun: 1.2 },
  { alt: 8, zenith: '#5d86bf', horizon: '#ead9cf', glow: '#ffc58a', belt: '#d6dbe8', hemiSky: '#c6d6ee', hemiGround: '#e4e3e6', hemi: 1.25, sun: 2.6 },
  { alt: 25, zenith: '#4b7cc0', horizon: '#d6e3f0', glow: '#fff1d8', belt: '#dbe6f2', hemiSky: '#d5e3f5', hemiGround: '#eef0f3', hemi: 1.3, sun: 3.2 },
];

// ---- the night's colour and light (owner's pick 2026-10-28: D winter, B autumn; the slider page
// https://claude.ai/artifact/3h2ciSuPe1y8AwnkPA2me7). Per season in resort.json grade.night, else NIGHT_LIGHT (the
// winter pick, every map and every season without its own):
//   blue: % of the way from the old navy night (snow ~4x more blue than red) toward a neutral one, on the scale of
//     the 2026-10-25 pick (40 %, which greyed the land); the moonlight's colour and strength and the night exposure
//     follow it (nightLight).
//   sky: x the night sky's horizon band (horizon, glow, belt; the fog takes the same colour), so the far land, fading
//     into the fog, meets the sky.
//   lift (render.js setGrade; a season's grade.night.lift replaces it): the night's dark tones raised in the picture,
//     on brightness only (the colour kept), fading out with distance from fade[0] x fog.near to fade[1] x fog.far
//     (main.js seasonLight), so the far land meets the sky unlifted. Before: 0.12 per colour channel at any distance
//     greyed the land, and the sky stayed dark above it (a hard line at the horizon).
const NAVY = [ // the old navy night (before 2026-10-25)
  { alt: -24, zenith: '#02040b', horizon: '#081227', glow: '#081227', belt: '#081227', hemiSky: '#253a66', hemiGround: '#1b2742', hemi: 0.55, sun: 0 },
  { alt: -14, zenith: '#050b1c', horizon: '#11234a', glow: '#1d2a55', belt: '#16244a', hemiSky: '#2f4a80', hemiGround: '#22324f', hemi: 0.65, sun: 0 },
];
const AT40 = [ // 40 % of the way toward neutral (the 2026-10-25 pick): the step `blue` is measured on
  { zenith: '#020409', horizon: '#091121', glow: '#091121', belt: '#091121', hemiSky: '#2f3e5d', hemiGround: '#21293c', hemi: 0.57 },
  { zenith: '#050b1a', horizon: '#122141', glow: '#1d284d', belt: '#172344', hemiSky: '#384e7e', hemiGround: '#27344e', hemi: 0.67 },
];
const byte = (h, i) => parseInt(h.slice(1 + 2 * i, 3 + 2 * i), 16);
const hexOf = (f) => '#' + [0, 1, 2].map((i) => Math.min(255, Math.max(0, Math.round(f(i)))).toString(16).padStart(2, '0')).join('');
const towards = (a, b, f) => hexOf((i) => byte(a, i) + (byte(b, i) - byte(a, i)) * f); // sRGB bytes, as the options were made
/** The night's light for a night blue (main.js applyTime): moonlight colour and strength, exposure added at night
 *  (both x skyAt().night). */
export function nightLight(blue) {
  const f = blue / 40;
  return { moon: parseInt(towards('#9fb8ff', '#afc1f1', f).slice(1), 16), moonK: 0.4 + 0.02 * f, exposure: 0.35 + 0.02 * f };
}
export const NIGHT_LIGHT = { blue: 30, sky: 1.6, lift: 0.03, fade: [0.15, 0.25] };
Object.assign(NIGHT_LIGHT, nightLight(NIGHT_LIGHT.blue)); // moon, moonK, exposure of the default night
const nightKeys = new Map();
/** Every keyframe for a night blue and sky (cached: a map has one or two). */
function keysFor(blue, sky) {
  const id = `${blue},${sky}`;
  if (!nightKeys.has(id)) {
    const f = blue / 40, night = NAVY.map((n, i) => {
      const k = { ...n, hemi: n.hemi + (AT40[i].hemi - n.hemi) * f };
      for (const c of ['zenith', 'horizon', 'glow', 'belt', 'hemiSky', 'hemiGround']) k[c] = towards(n[c], AT40[i][c], f);
      for (const c of ['horizon', 'glow', 'belt']) k[c] = hexOf((j) => byte(k[c], j) * sky);
      return k;
    });
    nightKeys.set(id, [...night, ...DAY_KEYS]);
  }
  return nightKeys.get(id);
}
// ---- the night's colour and light (end)

const cA = new THREE.Color(), cB = new THREE.Color();
/** The sky and its light at a sun altitude (deg). N: the season's grade.night ({ blue, sky }; absent = NIGHT_LIGHT). */
export function skyAt(alt, N) {
  const KEYS = keysFor(N?.blue ?? NIGHT_LIGHT.blue, N?.sky ?? NIGHT_LIGHT.sky);
  let i = 0;
  while (i < KEYS.length - 2 && alt > KEYS[i + 1].alt) i++;
  const a = KEYS[i], b = KEYS[i + 1];
  const t = THREE.MathUtils.clamp((alt - a.alt) / (b.alt - a.alt), 0, 1);
  const mix = (k) => cA.set(a[k]).lerp(cB.set(b[k]), t).clone();
  return {
    zenith: mix('zenith'), horizon: mix('horizon'), glow: mix('glow'), belt: mix('belt'),
    hemiSky: mix('hemiSky'), hemiGround: mix('hemiGround'),
    hemi: a.hemi + (b.hemi - a.hemi) * t,
    sun: a.sun + (b.sun - a.sun) * t,
    // Warm low sun, whiter higher up.
    sunColor: new THREE.Color().setHSL(0.08, 0.9, THREE.MathUtils.clamp(0.55 + alt / 60, 0.55, 0.92)),
    night: THREE.MathUtils.clamp((-alt - 6) / 8, 0, 1),        // 0 by day, 1 when properly dark
    // window and path lights (and the night bloom, fires, tracks): off while the sun is up, on through the blue hour,
    // full at -6 deg (owner 2026-10-26: lit rooms showed by day; was 6 to -4 deg, i.e. most of a Lapland winter day)
    lights: THREE.MathUtils.clamp(-alt / 6, 0, 1),
  };
}

// ---- VIS-243 the sun sets behind the horizon (begin): every map, both light paths (main.js applyTime, daylight.js).
// The sun's direct light, its disc in the sky and in the reflections fade as the real disc (0.53 deg) sinks behind the
// horizon, at the sun's drawn altitude (apparent with render.daylight). Before, the light ended in one frame at sunset.
export const SUN_DISC_R = 0.27; // deg: the sun's angular radius
const SIN_DISC_R = Math.sin(SUN_DISC_R * RAD);
/** 0..1: the share of the sun's disc above the horizon at a sun altitude (deg). */
export const sunDisc = (alt) => THREE.MathUtils.smoothstep(alt, -SUN_DISC_R, SUN_DISC_R);
/** 0..1: the sun light's strength at a sun altitude (deg): the ramp from -1 to 3 deg (the keyframes' light was tuned
 *  with it) x the disc. main.js shadows switch off where it is ~0 (setSunUp). */
export const sunFade = (alt) => THREE.MathUtils.clamp((alt + 1) / 4, 0, 1) * sunDisc(alt);
/** Exposure added in twilight: +0.15 between 0 and -8 deg, eased in from +0.5 to -1 deg and out from -6.5 to -9.5 deg
 *  (VIS-165; a hard step at 0 and -8 deg on every map before VIS-243). */
export const exposureBump = (alt) => 0.15 * THREE.MathUtils.smoothstep(-alt, -0.5, 1) * THREE.MathUtils.smoothstep(alt, -9.5, -6.5);
// ---- VIS-243 (end)

// ---- WS-66 the sun's look (begin) ------------------------------------------------------------------------------
// How the sun itself is drawn in the sky: resort.json -> sky.sun = a look name, or { look, ...overrides }. Every map
// gets `height` (SUN_DEFAULT; owner's pick of option C on web/previews/sun/, 2026-10-16, VIS-109): a sun near its real
// size (0.27 deg radius) at a radiance just over the bloom threshold, so the bloom gives it a tight rim instead of a
// blob, and the soft glow around it drawn in the sky itself, where its size is set here and not by the bloom (the glow
// stays under the threshold); tight high up, warmer and wider towards the horizon. `classic` is the sun up to
// 2026-10-16 (a disc of about 2.1 deg radius, 8x the real sun, at 4x sky brightness, which the bloom spread into a
// wide halo); `crisp` and `glare` were options A and B.
//   r, core   the disc's outer and inner radius (deg): soft edge between them
//   k         the disc's radiance, added to the sky (scene-linear; the bloom threshold is ~0.85-0.92)
//   glare     [radiance at the centre, width (deg)]: the bright circumsolar glow, exp(-angle / width)
//   aureole   [radiance, width (deg)]: the wide, faint aureole
//   low       { from (deg), k, glare, width, warm }: towards the horizon (full at 0 deg, none at `from`) the disc's
//             radiance and the glow are multiplied (k, glare), the glow widens (width) and both warm up (warm 0..1)
// The sky reflections (scene.environment) keep the classic disc whatever the look: they are the snow's and the
// glass's sparkle, approved as they are (main.js bakeEnv).
const deg = (c) => Math.acos(c) / RAD;
export const SUN_LOOKS = {
  classic: { r: deg(0.9993), core: deg(0.9998), k: 4.0, glare: [0, 1], aureole: [0, 1], low: null },
  crisp: { r: 0.55, core: 0.35, k: 2.4, glare: [0, 1], aureole: [0, 1], low: null },
  glare: { r: 0.55, core: 0.35, k: 4.0, glare: [0.3, 0.5], aureole: [0.12, 4], low: null },
  height: { r: 0.55, core: 0.35, k: 4.0, glare: [0.2, 0.35], aureole: [0.05, 2.5], low: { from: 14, k: 1, glare: 2.2, width: 2.5, warm: 1 } },
};
export const SUN_DEFAULT = 'height';
/** The look for resort.json -> sky.sun (a name, or { look, ...fields }); SUN_DEFAULT when unset or unknown. */
export function sunLookOf(cfg) {
  const name = typeof cfg === 'string' ? cfg : cfg?.look;
  const base = SUN_LOOKS[name] || SUN_LOOKS[SUN_DEFAULT];
  return cfg && typeof cfg === 'object' ? { ...base, ...cfg } : base;
}
const DISC_TINT = new THREE.Color(1.0, 0.85, 0.6), LOW_TINT = new THREE.Color(1.0, 0.5, 0.2), _tint = new THREE.Color();
/** The sky uniforms of the sun's look at a sun altitude (deg): sunShape, sunGlare, sunTint, sunGlow (main.js skyUniforms). */
export function setSunLook(u, look, alt) {
  const L = look.low, w = L ? 1 - THREE.MathUtils.smoothstep(alt, 0, L.from) : 0;
  const m = (f) => (L ? 1 + ((L[f] ?? 1) - 1) * w : 1);
  u.sunShape.value.set(Math.cos(look.r * RAD), Math.cos(look.core * RAD), look.k * m('k'), 0);
  u.sunGlare.value.set(look.glare[0] * m('glare'), look.glare[1] * m('width') * RAD, look.aureole[0] * m('glare'), look.aureole[1] * m('width') * RAD);
  _tint.copy(DISC_TINT).lerp(LOW_TINT, L ? (L.warm ?? 0) * w : 0);
  u.sunTint.value.copy(_tint);
  u.sunGlow.value.copy(_tint);
}
// ---- WS-75 sky (begin): the sky as node materials (TSL). The sky's uniforms are TSL uniform nodes: main.js (and the
// previews) keep writing `.value` on them where they always did, and every material that draws the sky's colour (the
// dome, the far landscape's rim, the air in lazy/atmos.js, the water's clock) uses the same nodes: one source.
/** The sky's uniforms (TSL uniform nodes; `.value` as before), with the default sun look until main.js sets the map's
 *  own: zenith, horizon, glow, belt, sunDir, time; moonDir, moonK, moonSize, moonHalo (nightsky.js); sunShape,
 *  sunGlare, sunTint, sunGlow (WS-66, setSunLook). */
export function createSkyUniforms() {
  // VIS-267 (rescue night): the shared render group, uploaded once a render on every set (the far landscape's rim and
  // the water read them too; in their own group three left them at the last value it saw them change)
  const live = (v) => uniform(v).setGroup(renderGroup);
  const u = {
    zenith: live(new THREE.Color()), horizon: live(new THREE.Color()), glow: live(new THREE.Color()), belt: live(new THREE.Color()),
    sunDir: live(new THREE.Vector3(0, 1, 0)), time: live(0),
    moonDir: live(new THREE.Vector3(0, -1, 0)), moonK: live(0), moonSize: live(0.007), moonHalo: live(0),
    sunShape: live(new THREE.Vector4()), sunGlare: live(new THREE.Vector4()), sunTint: live(new THREE.Color()), sunGlow: live(new THREE.Color()),
  };
  setSunLook(u, SUN_LOOKS[SUN_DEFAULT], 30);
  return u;
}
// The glow around the sun, shared by the sky and the far landscape's rim (landscape.js), so where the land dissolves
// into the sky under a low sun it takes the same glow and draws no line. Arguments stay in range (WS-15): the angle is
// the chord (sqrt of a clamped value), the widths are floored. d, sunDir: vec3 nodes; g: vec4 (sunGlare); tint: vec3.
export const sunGlowAt = (d, sunDir, g, tint) => {
  const a = sqrt(max(float(2).mul(float(1).sub(dot(d, normalize(sunDir)))), 0));
  const k = g.x.mul(exp(a.negate().div(max(g.y, 1e-4)))).add(g.z.mul(exp(a.negate().div(max(g.w, 1e-4)))));
  return tint.mul(k).mul(smoothstep(-0.03, 0.01, sunDir.y));
};
// ---- WS-66 the sun's look (end) --------------------------------------------------------------------------------

/** The sky's colour in direction d (unit vec3 node) before the sun and moon: horizon to zenith, the twilight glow on
 *  the sun side, the belt of Venus opposite. Shared by the dome and lazy/atmos.js (aerial perspective). */
export const skyGradient = (U, d) => {
  const h = clamp(d.y, -0.2, 1.0), hp = max(h, 0);
  const col = mix(U.horizon, U.zenith, pow(hp, 0.5)).toVar();
  const sd = normalize(U.sunDir.xz.add(1e-5)), dd = normalize(d.xz.add(1e-5));
  const toward = clamp(dot(sd, dd).mul(0.5).add(0.5), 0, 1); // WS-15: rounding can leave [0,1]; pow(<0) = NaN
  const low = pow(float(1).sub(hp), 5);
  col.addAssign(U.glow.mul(low).mul(pow(toward, 3)).mul(0.9)); // twilight glow on the sun side
  col.assign(mix(col, U.belt, low.mul(pow(float(1).sub(toward), 2)).mul(0.55))); // belt of Venus opposite
  return col;
};
/** Below the horizon the sky fades to a darker horizon colour (the dome's last step; atmos uses it too). */
export const skyBelow = (U, d, col) => {
  const c = mix(col, U.horizon.mul(0.85), float(1).sub(smoothstep(-0.15, 0, d.y)));
  return U.below ? U.below(d, c) : c; // sky.style physical (lazy/skyphys.js): what the dome shows there, also behind the far land's rim; absent = no node
};

/** The sky dome's colour (vec3 node) for direction d: gradient, the sun (WS-66 look), the moon with its phase. */
export const skyColor = (U, d) => {
  const col = skyGradient(U, d);
  const sunN = normalize(U.sunDir);
  // unset uniforms (sunShape.z = 0): no disc
  const disc = select(U.sunShape.z.greaterThan(0), smoothstep(U.sunShape.x, U.sunShape.y, dot(d, sunN)), 0);
  col.addAssign(U.sunTint.mul(disc).mul(U.sunShape.z).mul(smoothstep(-SIN_DISC_R, SIN_DISC_R, U.sunDir.y))); // VIS-243: sets with the light (sunDisc)
  col.addAssign(sunGlowAt(d, U.sunDir, U.sunGlare, U.sunGlow)); // WS-66: the glow round the sun (0 in the classic look)
  // the moon: a disc lit by the sun (phase and the tilt of its terminator follow from the two directions), flat-looking
  // like the real one (Lommel-Seeliger), faint earthshine on the dark side, warmer low down, and the glow of moonlight
  // scattered in the air around it. Its disc's screen derivative is taken outside the branch (WGSL: derivatives only
  // in uniform control flow); the branch on moonK is uniform.
  const cm = dot(d, U.moonDir);
  const u = d.sub(U.moonDir.mul(cm)).div(max(U.moonSize, 1e-5));
  const rr = dot(u, u), fw = fwidth(rr);
  If(U.moonK.greaterThan(0), () => {
    const ang = sqrt(max(float(2).mul(float(1).sub(cm)), 0));
    const tint = mix(vec3(1.0, 0.72, 0.48), vec3(1.0, 0.97, 0.9), smoothstep(0, 0.3, U.moonDir.y));
    col.addAssign(tint.mul(vec3(0.75, 0.82, 1.0)).mul(U.moonHalo).mul(exp(ang.negate().div(0.05)).mul(0.5).add(exp(ang.negate().div(0.3)).mul(0.5))));
    const z = sqrt(max(float(1).sub(rr), 0));
    const n = u.sub(U.moonDir.mul(z)); // surface normal, facing us
    const mu0 = max(dot(n, sunN), 0), mu = max(z, 1e-3);
    const lit = mu0.mul(2).div(mu0.add(mu));
    const edge = float(1).sub(smoothstep(float(1).sub(fw.mul(2)), 1, rr)).mul(step(rr, 1.4));
    col.addAssign(tint.mul(lit.mul(1.1).add(0.02)).mul(U.moonK).mul(edge));
  });
  return skyBelow(U, d, col);
};

/** Sky-dome vertices on the far plane (z = w), so the dome never clips and never hides anything. */
export const farPlaneVertex = () => {
  const p = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(positionGeometry, 1)));
  return vec4(p.xy, p.w, p.w);
};

/** The sky dome's node material (main.js: the dome and the reflections' bake, envScene; the clouds preview). */
export function createSkyMaterial(U) {
  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide, depthWrite: false, fog: false });
  m.vertexNode = farPlaneVertex();
  m.colorNode = Fn(() => skyColor(U, normalize(positionGeometry)))();
  return m;
}
// ---- WS-75 sky (end)
